import { useCallback, useEffect, useRef, useState } from 'react';
import type { NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import Animated, {
  type SharedValue,
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { Flex, RemoteImage, Typography, ColorValues } from '@banhala/ui-kit';
import type { RollingBannerListProps, RollingBannerItem } from './types';
import { useLogClickBanner } from './hooks/useLogClickBanner';
import { useLogDisplayBanner } from './hooks/useLogDisplayBanner';
import {
  useForegroundInterval,
  useGuardedScreenWidth,
  useOpenDeeplink,
} from '../../../../hooks';

// iOS MetaFeedRollingBannerCell (itemWidth = screenWidth - 64, FSPagerView itemSpacing=0) 매칭.
// Android RollingBannerListSlideQueue 의 pageSpacing=10 과는 다름 — cardWidth 가 네이티브와
// 동일해야 한다는 제약 (양 옆 inset 32) 하에 양 옆 노출 폭을 최대로 확보하려면 spacing=0.
const HORIZONTAL_PADDING = 32;
const PAGE_SPACING = -15;
const AUTO_SCROLL_INTERVAL = 5000;
const SCALE_CENTER = 1;
const SCALE_ADJACENT = 0.83;

/**
 * parallax carousel — body.item.view_type === 'SLIDE_QUEUE' 케이스.
 * iOS/Android 네이티브와 동일한 layout (가로 32 padding + 카드 사이 10 spacing).
 *
 * 이전엔 react-native-reanimated-carousel 기반 ui-kit Carousel 을 사용했으나
 * Android 에서 부모 vertical scroll 컨테이너 안에 nested 됐을 때 RNGH PanGesture
 * 가 ACTION_MOVE 단계에서 시작도 못 하는 이슈가 있어 RN ScrollView 로 재구현.
 * 무한 스크롤은 entity 앞뒤에 clone 을 추가해 경계 도달 시 animated:false 로
 * 점프 — 마지막→첫 전환이 역방향 애니메이션 없이 자연스럽게 이어진다.
 */
export const SlideQueueBanner = ({
  entity,
  logging,
  logDisabled,
  isVisible = true,
}: RollingBannerListProps) => {
  const screenWidth = useGuardedScreenWidth('SlideQueueBanner');
  const { openDeeplink } = useOpenDeeplink();
  const { logClickBannerItem } = useLogClickBanner(logging);

  const itemCount = entity.length;
  const isInfinite = itemCount > 1;

  const cardWidth = screenWidth ? screenWidth - HORIZONTAL_PADDING * 2 : 0;
  const pageStep = cardWidth + PAGE_SPACING;

  // 무한 스크롤: [last clone, ...entity, first clone]
  const infiniteItems = isInfinite
    ? [entity[itemCount - 1], ...entity, entity[0]]
    : entity;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollX = useSharedValue(0);
  const scrollRef = useRef<Animated.ScrollView>(null);

  useLogDisplayBanner({ entity, logging, currentIndex });

  // 초기 위치: clone last 다음의 실제 첫 카드 = ScrollView index 1
  useEffect(() => {
    if (isInfinite && pageStep > 0) {
      scrollRef.current?.scrollTo({ x: pageStep, animated: false });
    }
  }, [isInfinite, pageStep]);

  const handleBannerPress = useCallback(
    (item: RollingBannerItem) => {
      if (!item.item.deeplink) return;
      logClickBannerItem(item.logging);
      openDeeplink(item.item.deeplink);
    },
    [openDeeplink, logClickBannerItem],
  );

  const onScroll = useAnimatedScrollHandler({
    onScroll: e => {
      scrollX.value = e.contentOffset.x;
    },
  });

  const handleScrollBeginDrag = useCallback(() => setIsDragging(true), []);
  const handleScrollEndDrag = useCallback(() => setIsDragging(false), []);

  // 경계 도달 시 animated:false 로 점프해서 무한 스크롤 시 역방향 애니메이션을 막는다.
  const handleMomentumScrollEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (pageStep <= 0) return;
      const scrollIndex = Math.round(e.nativeEvent.contentOffset.x / pageStep);

      if (isInfinite) {
        if (scrollIndex === 0) {
          // clone last 도달 → 실제 last 로 점프
          scrollRef.current?.scrollTo({
            x: itemCount * pageStep,
            animated: false,
          });
          setCurrentIndex(itemCount - 1);
          return;
        }
        if (scrollIndex === itemCount + 1) {
          // clone first 도달 → 실제 first 로 점프
          scrollRef.current?.scrollTo({ x: pageStep, animated: false });
          setCurrentIndex(0);
          return;
        }
        setCurrentIndex(scrollIndex - 1);
      } else {
        setCurrentIndex(Math.min(itemCount - 1, Math.max(0, scrollIndex)));
      }
    },
    [pageStep, isInfinite, itemCount],
  );

  // auto-scroll 은 항상 우측 방향으로 한 페이지 — 경계 처리는 momentum end 에서 점프.
  const autoScroll = useCallback(() => {
    if (pageStep <= 0) return;
    if (isInfinite) {
      // ScrollView index = entity index + 1 (앞 clone 보정). 다음 = +1.
      const targetScrollIndex = currentIndex + 2;
      scrollRef.current?.scrollTo({
        x: targetScrollIndex * pageStep,
        animated: true,
      });
    } else {
      const next = (currentIndex + 1) % itemCount;
      scrollRef.current?.scrollTo({ x: next * pageStep, animated: true });
    }
  }, [currentIndex, itemCount, isInfinite, pageStep]);

  // isVisible / dragging / logDisabled 조건이 깨지면 interval 자체를 멈춘다 (callback 안에서
  // skip 하는 게 아니라 enabled=null 로 interval 정지). swipe 중엔 timer 가 reset되고
  // swipe 끝나면 다시 5초 카운트 시작 — 사용자가 swipe 직후 곧바로 auto-scroll 이 발생하는 것을 방지.
  useForegroundInterval(
    autoScroll,
    isVisible && isInfinite && !logDisabled && !isDragging
      ? AUTO_SCROLL_INTERVAL
      : null,
    { immediate: false },
  );

  if (itemCount === 0) return null;
  if (!screenWidth) return null;

  return (
    <Flex position={'relative'} padding={{ top: 12 }}>
      <Animated.ScrollView
        ref={scrollRef}
        horizontal
        snapToInterval={pageStep}
        // 빠른 swipe 시 momentum velocity 가 커서 두세 페이지 한 번에 넘어가는 것을 막고
        // 한 swipe 당 한 page 만 이동하도록 제한.
        disableIntervalMomentum
        // 'fast' (iOS 0.99 / Android 0.9) 보다 더 빠른 decel — auto-scroll 의
        // scrollTo({animated:true}) 속도와 비슷하게 매칭. swipe 후 곧바로 snap.
        decelerationRate={0.5}
        contentContainerStyle={{ paddingHorizontal: HORIZONTAL_PADDING }}
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        onScrollBeginDrag={handleScrollBeginDrag}
        onScrollEndDrag={handleScrollEndDrag}
        onMomentumScrollEnd={handleMomentumScrollEnd}
        scrollEventThrottle={16}
      >
        {infiniteItems.map((bannerItem, index) => (
          <ParallaxItem
            key={`slide-${index}`}
            index={index}
            scrollX={scrollX}
            pageStep={pageStep}
            cardWidth={cardWidth}
            isFirst={index === 0}
            uri={bannerItem.item.image_url}
            onPress={() => handleBannerPress(bannerItem)}
            testID={`롤링배너?index=${index}`}
          />
        ))}
      </Animated.ScrollView>
      <Flex.Row
        position={'absolute'}
        bottom={32}
        right={48}
        borderRadius={20}
        backgroundColorValue={ColorValues.alpha_dark12}
        padding={{ horizontal: 6, vertical: 4 }}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Typography type={'body4'} color={'content_always_white'}>
          {currentIndex + 1}
        </Typography>
        <Flex
          width={1}
          height={8}
          backgroundColor={'white'}
          margin={{ horizontal: 4 }}
        />
        <Typography type={'body4'} rawColor={`#FFFFFF99`}>
          {entity.length}
        </Typography>
      </Flex.Row>
    </Flex>
  );
};

const ParallaxItem = ({
  index,
  scrollX,
  pageStep,
  cardWidth,
  isFirst,
  uri,
  onPress,
  testID,
}: {
  index: number;
  scrollX: SharedValue<number>;
  pageStep: number;
  cardWidth: number;
  isFirst: boolean;
  uri: string;
  onPress: () => void;
  testID: string;
}) => {
  const animatedStyle = useAnimatedStyle(() => {
    const center = index * pageStep;
    const offset = scrollX.value - center;
    const distance = Math.abs(offset / pageStep);
    const scale = interpolate(
      distance,
      [0, 1],
      [SCALE_CENTER, SCALE_ADJACENT],
      Extrapolation.CLAMP,
    );
    return { transform: [{ scale }] };
  });

  return (
    <Animated.View
      style={[
        {
          width: cardWidth,
          marginLeft: isFirst ? 0 : PAGE_SPACING,
          alignItems: 'center',
          justifyContent: 'center',
        },
        animatedStyle,
      ]}
    >
      <Flex.Center onPress={onPress} testID={testID}>
        <RemoteImage
          uri={uri}
          width={cardWidth}
          height={cardWidth}
          borderRadius={'xlarge'}
        />
      </Flex.Center>
    </Animated.View>
  );
};
